/**
 * usePushNotifications — handles VAPID-based Web Push subscribe/unsubscribe
 * against /api/notifications/*. Works in PWA-installed mode AND in the
 * regular browser (Chrome/Edge/Firefox; Safari 16.4+).
 */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function usePushNotifications() {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const ok =
      typeof window !== "undefined" &&
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window;
    setSupported(ok);
    if (!ok) return;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setSubscribed(!!sub))
      .catch(() => setSubscribed(false));
  }, []);

  const enable = useCallback(async () => {
    if (!supported) {
      toast.error("Push notifications aren't supported in this browser.");
      return false;
    }
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Notification permission denied.");
        return false;
      }
      const { data } = await api.get("/notifications/vapid-key");
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.key),
      });
      const json = sub.toJSON();
      await api.post("/notifications/subscribe", {
        endpoint: json.endpoint,
        keys: json.keys,
      });
      setSubscribed(true);
      toast.success("Notifications on — we'll ping you for price drops & restocks.");
      return true;
    } catch (e) {
      console.error(e);
      toast.error("Couldn't enable notifications: " + (e?.message || "unknown"));
      return false;
    } finally {
      setLoading(false);
    }
  }, [supported]);

  const disable = useCallback(async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api
          .post("/notifications/unsubscribe", { endpoint: sub.endpoint })
          .catch(() => {});
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Notifications off.");
    } catch (e) {
      toast.error("Couldn't disable: " + (e?.message || "unknown"));
    } finally {
      setLoading(false);
    }
  }, []);

  const sendTest = useCallback(async () => {
    try {
      await api.post("/notifications/test", {});
      toast.success("Test sent — check your notifications.");
    } catch (e) {
      toast.error("Couldn't send test: " + (e?.message || "unknown"));
    }
  }, []);

  return { supported, permission, subscribed, loading, enable, disable, sendTest };
}
